/**
 * PublicProfilePage - Read-Only Profile View
 * 
 * Shows another user's profile in read-only mode with messaging action.
 * Based on ProfilePage layout but without edit capabilities.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7D
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePublicProfile } from '../hooks/useProfile';
import { useAuth } from '../hooks/useAuth';
import { TopBar, Avatar, UserBadge, Button, EmptyState, ProfileHeaderSkeleton, ActivityCardSkeleton } from '../components';
import { createPublicProfileMobileMenu } from '../components/MobileContextMenu';
import { MessageCircle } from 'lucide-react';

export default function PublicProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'activity' | 'clubs' | 'stats'>('activity');
  
  // Fetch public profile using React Query
  const { data: profile, isLoading, error: fetchError } = usePublicProfile(id || '');
  
  // Create mobile menu configuration
  const publicProfileMenu = createPublicProfileMobileMenu(navigate, logout);
  
  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Profile" onBack={() => navigate(-1)} mobileMenu={publicProfileMenu} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-6 space-y-6">
            <ProfileHeaderSkeleton />
            
            {/* Activity section skeleton */}
            <div className="space-y-4">
              <div className="h-6 w-32 bg-oro-surface-alt rounded animate-pulse" />
              {[1, 2, 3].map((i) => (
                <ActivityCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (fetchError || !profile || !id) {
    const errorMessage = fetchError 
      ? 'User not found'
      : !id 
      ? 'Invalid user ID'
      : 'Failed to load profile';
      
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Profile" onBack={() => navigate(-1)} mobileMenu={publicProfileMenu} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12">
            <EmptyState
              icon="👤"
              title="Profile not found"
              message={errorMessage}
              actionLabel="Go Back"
              onAction={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar 
        title={profile.public_display_name || 'Profile'} 
        onBack={() => navigate(-1)} 
        mobileMenu={publicProfileMenu}
      />
      
      <div className="flex-1 overflow-y-auto">
        {/* Hero Section */}
        <div className="relative">
          {/* Cover Photo */}
          <div className="h-40 bg-linear-to-br from-accent/20 to-accent/5 relative overflow-hidden">
            {/* Topo Pattern Overlay */}
            <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
              <defs>
                <pattern id="topo-public" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                  <circle cx="10" cy="10" r="4" fill="none" stroke="currentColor" strokeWidth="0.3"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#topo-public)"/>
            </svg>
          </div>

          {/* Avatar Card - Overlapping cover */}
          <div className="px-4 -mt-12 relative z-10">
            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 shadow-card">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar
                    name={profile.public_display_name}
                    profile_image_url={profile.profile_image_url}
                    user_level={profile.user_level}
                    size="lg"
                    showGoldRing={true}
                    className="ring-2 ring-oro-bg"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-xl font-bold text-text-primary truncate">
                      {profile.public_display_name}
                    </h1>
                    <UserBadge user_level={profile.user_level} size="sm" />
                  </div>
                  {profile.tagline && (
                    <p className="text-sm text-text-secondary mb-2 line-clamp-2">
                      {profile.tagline}
                    </p>
                  )}
                  {profile.location && (
                    <p className="text-xs text-text-muted">📍 {profile.location}</p>
                  )}
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    // TODO: Implement messaging
                    // Future feature: Implement direct messaging functionality
                  }}
                  className="shrink-0"
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  Message
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-6 space-y-6">
          {/* Stats Row */}
          <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
            <div className="flex items-center justify-around">
              <div className="text-center">
                <div className="text-xl font-bold text-text-primary">12</div>
                <div className="text-xs text-text-muted">Events</div>
              </div>
              <div className="w-px h-8 bg-oro-border"></div>
              <div className="text-center">
                <div className="text-xl font-bold text-text-primary">3</div>
                <div className="text-xs text-text-muted">Clubs</div>
              </div>
              <div className="w-px h-8 bg-oro-border"></div>
              <div className="text-center">
                <div className="text-xl font-bold text-text-primary">8</div>
                <div className="text-xs text-text-muted">Achievements</div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 bg-oro-surface-alt border border-oro-border rounded-xl p-1">
            {[
              { key: 'activity' as const, label: 'Activity' },
              { key: 'clubs' as const, label: 'Clubs' },
              { key: 'stats' as const, label: 'Stats' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-oro-surface text-text-primary shadow-sm'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'activity' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Recent Activity</h3>
              <EmptyState
                icon="🔒"
                title="Private activity"
                message="This user's recent activities are private"
              />
            </div>
          )}

          {activeTab === 'clubs' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Club Memberships</h3>
              <EmptyState
                icon="👥"
                title="Private club info"
                message="Club memberships are private"
              />
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Stats & Insights</h3>
              <EmptyState
                icon="📈"
                title="Stats coming soon"
                message="Profile analytics will be available soon"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}